import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { BillingDashboard } from '../../types/Billing';

interface BillingOverviewProps {
  dashboardData: BillingDashboard | null;
}

const BillingOverview: React.FC<BillingOverviewProps> = ({ dashboardData }) => {
  if (!dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No dashboard data available
        </Typography>
      </Box>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'payment':
        return <PaymentIcon color="success" />;
      case 'charge':
        return <ReceiptIcon color="primary" />;
      case 'denial':
        return <WarningIcon color="error" />;
      case 'claim_submission':
        return <CheckCircleIcon color="info" />;
      default:
        return <InfoIcon />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'payment':
        return 'success';
      case 'charge':
        return 'primary';
      case 'denial':
        return 'error';
      case 'claim_submission':
        return 'info';
      default:
        return 'default';
    }
  };

  const totalAgingBalance = Object.values(dashboardData.agingBuckets).reduce((sum, balance) => sum + balance, 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Billing Overview Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                Performance Metrics
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Clean Claim Rate</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPercentage(dashboardData.cleanClaimRate)}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={dashboardData.cleanClaimRate * 100} 
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                  color={dashboardData.cleanClaimRate >= 0.95 ? 'success' : dashboardData.cleanClaimRate >= 0.90 ? 'warning' : 'error'}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Collection Rate</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPercentage(dashboardData.collectionRate)}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={dashboardData.collectionRate * 100} 
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                  color={dashboardData.collectionRate >= 0.95 ? 'success' : dashboardData.collectionRate >= 0.90 ? 'warning' : 'error'}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Denial Rate</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPercentage(dashboardData.denialRate)}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={dashboardData.denialRate * 100} 
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                  color={dashboardData.denialRate <= 0.05 ? 'success' : dashboardData.denialRate <= 0.10 ? 'warning' : 'error'}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2">Days in A/R</Typography>
                  <Chip 
                    label={`${dashboardData.daysInAR.toFixed(1)} days`}
                    color={dashboardData.daysInAR <= 30 ? 'success' : dashboardData.daysInAR <= 45 ? 'warning' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* A/R Aging Analysis */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingDownIcon sx={{ mr: 1 }} />
                A/R Aging Analysis
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Age Range</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell align="right">% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Current (0-30)</TableCell>
                      <TableCell align="right">{formatCurrency(dashboardData.agingBuckets.current)}</TableCell>
                      <TableCell align="right">
                        {formatPercentage(dashboardData.agingBuckets.current / totalAgingBalance)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>31-60 Days</TableCell>
                      <TableCell align="right">{formatCurrency(dashboardData.agingBuckets.thirtyDays)}</TableCell>
                      <TableCell align="right">
                        {formatPercentage(dashboardData.agingBuckets.thirtyDays / totalAgingBalance)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>61-90 Days</TableCell>
                      <TableCell align="right">{formatCurrency(dashboardData.agingBuckets.sixtyDays)}</TableCell>
                      <TableCell align="right">
                        {formatPercentage(dashboardData.agingBuckets.sixtyDays / totalAgingBalance)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>91-120 Days</TableCell>
                      <TableCell align="right">{formatCurrency(dashboardData.agingBuckets.ninetyDays)}</TableCell>
                      <TableCell align="right">
                        {formatPercentage(dashboardData.agingBuckets.ninetyDays / totalAgingBalance)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>120+ Days</TableCell>
                      <TableCell align="right">{formatCurrency(dashboardData.agingBuckets.over120)}</TableCell>
                      <TableCell align="right">
                        {formatPercentage(dashboardData.agingBuckets.over120 / totalAgingBalance)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Total A/R: {formatCurrency(totalAgingBalance)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Focus on accounts over 90 days: {formatCurrency(dashboardData.agingBuckets.ninetyDays + dashboardData.agingBuckets.over120)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Payers by Volume */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Payers by Volume
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payer</TableCell>
                      <TableCell align="right">Claims</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.topPayersByVolume.map((payer, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {payer.payerName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={payer.claimCount} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(payer.totalAmount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              
              <List dense>
                {dashboardData.recentActivity.map((activity, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getActivityIcon(activity.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" component="span">
                            {activity.description}
                          </Typography>
                          <Chip 
                            label={formatCurrency(activity.amount)}
                            size="small"
                            color={getActivityColor(activity.type) as any}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(activity.timestamp)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Daily Summary */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Financial Summary
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                    <Typography variant="h6">
                      {formatCurrency(dashboardData.dailyCharges)}
                    </Typography>
                    <Typography variant="body2">
                      Daily Charges
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
                    <Typography variant="h6">
                      {formatCurrency(dashboardData.dailyPayments)}
                    </Typography>
                    <Typography variant="body2">
                      Daily Payments
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'warning.light', borderRadius: 1, color: 'warning.contrastText' }}>
                    <Typography variant="h6">
                      {formatCurrency(dashboardData.dailyAdjustments)}
                    </Typography>
                    <Typography variant="body2">
                      Daily Adjustments
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'info.light', borderRadius: 1, color: 'info.contrastText' }}>
                    <Typography variant="h6">
                      {formatCurrency(dashboardData.dailyCharges - dashboardData.dailyPayments - dashboardData.dailyAdjustments)}
                    </Typography>
                    <Typography variant="body2">
                      Net Change
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingOverview;